import { Plus, Trash02 } from "@untitledui/icons";
import { useEffect, useId, useMemo } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { NativeSelect } from "@/components/base/select/select-native";
import { TextArea } from "@/components/base/textarea/textarea";
import { useAppForm } from "@/components/form";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import {
	type FormRule,
	type FormRuleAction,
	FormRuleActionType,
	type FormRuleActionTypeValue,
	type FormRuleCondition,
	FormRuleConditionLogic,
	FormRuleConditionOperator,
	type FormRuleConditionOperatorValue,
	getFormRuleOperatorsForDataType,
} from "../../types/form-rule-types";
import { FormRuleConditionLogicToggle } from "./form-rule-condition-logic-toggle";
import {
	getConditionValueInputKind,
	normalizeConditionValue,
} from "./form-rule-condition-value.util";
import {
	FormRuleOptionMultiValueInput,
	FormRuleOptionSingleValueInput,
	FormRuleTextValueInput,
} from "./form-rule-condition-value-input";
import { FormRulesFeaturedIcon } from "./form-rules-featured-icon";

function newId(): string {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export interface FormRuleEditorFieldOption {
	id: string;
	label: string;
	dataType: string | undefined;
	options?: { value: string; label: string }[];
}

export interface FormRuleEditorModalProps {
	isOpen: boolean;
	mode: "create" | "edit";
	initialRule: FormRule | null;
	fields: FormRuleEditorFieldOption[];
	paths: AdminFormI18nPaths;
	onSubmit: (rule: FormRule) => void;
	onCancel: () => void;
}

const DEFAULT_LOGIC = FormRuleConditionLogic.all;

/** Matches form rule modal mock: light gray card around each condition/action row. */
const RULE_ITEM_CARD_CLASS = "rounded-lg border border-gray-200 bg-gray-50 p-4";

const RULE_FIELD_CONTROL_CLASS = "w-full";

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

function buildDraft(initialRule: FormRule | null): FormRule {
	if (initialRule) {
		return {
			...initialRule,
			description: initialRule.description ?? "",
		};
	}
	return {
		id: newId(),
		name: "",
		description: "",
		enabled: true,
		conditionLogic: DEFAULT_LOGIC,
		conditions: [],
		actions: [
			{
				id: newId(),
				type: FormRuleActionType.showField,
				targetFieldDefinitionId: "",
			},
		],
	};
}

function isOperatorAllowedForField(
	operator: FormRuleConditionOperatorValue,
	dataType: string | undefined,
): boolean {
	const allowed = getFormRuleOperatorsForDataType(dataType);
	return allowed.includes(operator);
}

function ensureOperatorForField(
	operator: FormRuleConditionOperatorValue,
	dataType: string | undefined,
): FormRuleConditionOperatorValue {
	if (isOperatorAllowedForField(operator, dataType)) return operator;
	const allowed = getFormRuleOperatorsForDataType(dataType);
	return allowed[0] ?? FormRuleConditionOperator.equals;
}

/**
 * Wraps `useAppForm` so the row sub-components can pin their `form` prop type
 * via `ReturnType<typeof useFormRuleEditorForm>` without re-deriving the deep
 * generic signature that `useAppForm` returns.
 */
function useFormRuleEditorForm(
	initialRule: FormRule | null,
	actionsRequiredMessage: string,
	onSubmit: (rule: FormRule) => void,
) {
	return useAppForm({
		defaultValues: buildDraft(initialRule),
		validators: {
			onSubmit: ({ value }) => {
				if (value.actions.length === 0) return actionsRequiredMessage;
				return undefined;
			},
		},
		onSubmit: ({ value }) => {
			const now = new Date().toISOString();
			onSubmit({
				...value,
				name: value.name.trim(),
				description: value.description?.trim() || undefined,
				createdAt: value.createdAt ?? now,
				updatedAt: now,
			});
		},
	});
}

type FormRuleEditorForm = ReturnType<typeof useFormRuleEditorForm>;

/**
 * Centered modal editor for a single form rule. Built on TanStack Form via
 * `useAppForm` (mirrors the project's `create-sub-con.form.layout.tsx` pattern):
 * scalar fields use `form.AppField` + `field.InputField` with i18n
 * `validators.onChange`, the `conditions` and `actions` field arrays use
 * `<form.Field mode="array">` with `pushValue` / `removeValue`, cross-field
 * operator/value clearing is done imperatively via `form.setFieldValue`, and
 * the footer Save button + inline actions-required hint subscribe through
 * `form.Subscribe`. Conditions are optional; the All/Any logic selector is
 * always shown and defaults to `"all"`. Actions require at least one row.
 */
export function FormRuleEditorModal({
	isOpen,
	mode,
	initialRule,
	fields,
	paths,
	onSubmit,
	onCancel,
}: FormRuleEditorModalProps) {
	const { t } = useAppTranslation();
	const titleId = useId();

	const fieldsById = useMemo(() => {
		const map = new Map<string, FormRuleEditorFieldOption>();
		for (const f of fields) {
			map.set(f.id, f);
		}
		return map;
	}, [fields]);

	const fieldSelectOptions = useMemo(
		() => [
			{ value: "", label: t(paths.formRules.modal.selectField) },
			...fields.map((f) => ({ value: f.id, label: f.label })),
		],
		[fields, paths.formRules.modal.selectField, t],
	);

	const actionTypeOptions = useMemo(
		() => [
			{ value: "", label: t(paths.formRules.modal.selectAction) },
			...Object.values(FormRuleActionType).map((value) => ({
				value,
				label: t(paths.formRules.actions[value]),
			})),
		],
		[paths.formRules.actions, paths.formRules.modal.selectAction, t],
	);

	const operatorOptionsFor = (dataType: string | undefined) => {
		const operators = getFormRuleOperatorsForDataType(dataType);
		return [
			{ value: "", label: t(paths.formRules.modal.selectOperator) },
			...operators.map((op) => ({
				value: op,
				label: t(paths.formRules.operators[op]),
			})),
		];
	};

	const form = useFormRuleEditorForm(
		initialRule,
		t(paths.formRules.validation.actionsRequired),
		onSubmit,
	);

	useEffect(() => {
		if (isOpen) {
			form.reset(buildDraft(initialRule));
		}
	}, [isOpen, initialRule, form]);

	const modalTitle =
		mode === "create"
			? t(paths.formRules.modal.createTitle)
			: t(paths.formRules.modal.editTitle);

	const addCondition = () => {
		const c: FormRuleCondition = {
			id: newId(),
			fieldDefinitionId: "",
			operator: FormRuleConditionOperator.equals,
		};
		form.pushFieldValue("conditions", c);
	};

	const addAction = () => {
		const a: FormRuleAction = {
			id: newId(),
			type: FormRuleActionType.showField,
			targetFieldDefinitionId: "",
		};
		form.pushFieldValue("actions", a);
	};

	return (
		<AriaDialogTrigger
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<ModalOverlay isDismissable={false}>
				<Modal>
					<Dialog aria-labelledby={titleId}>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-lg">
							<CloseButton
								size="sm"
								label={t(paths.formRules.modal.close)}
								onPress={onCancel}
								className="absolute top-4 right-4 z-10"
							/>

							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<FormRulesFeaturedIcon className="mx-auto mb-2" />
								<div className="z-10 flex flex-col gap-0.5 text-center">
									<AriaHeading
										slot="title"
										id={titleId}
										className="text-md font-semibold text-primary"
									>
										{modalTitle}
									</AriaHeading>
									<p className="text-sm text-tertiary">
										{t(paths.formRules.modal.subtitle)}
									</p>
								</div>
							</div>

							<div className="max-h-[60vh] overflow-y-auto px-4 pb-2 pt-5 sm:px-6">
								<form
									className="flex flex-col gap-5"
									onSubmit={(e) => {
										e.preventDefault();
										e.stopPropagation();
										form.handleSubmit();
									}}
								>
									<form.AppField
										name="name"
										validators={{
											onChange: ({ value }: { value: string }) =>
												value.trim().length === 0
													? t(paths.formRules.validation.nameRequired)
													: undefined,
										}}
										children={(field) => (
											<field.InputField
												label={t(paths.formRules.modal.name)}
												placeholder={t(paths.formRules.modal.namePlaceholder)}
												isRequired
												maxLength={NAME_MAX_LENGTH}
												hint={
													<span className="block text-right text-xs text-text-tertiary">
														{(field.state.value as string | undefined)
															?.length ?? 0}
														/{NAME_MAX_LENGTH}
													</span>
												}
											/>
										)}
									/>

									<form.AppField
										name="description"
										children={(field) => (
											<TextArea
												label={t(paths.formRules.modal.description)}
												placeholder={t(
													paths.formRules.modal.descriptionPlaceholder,
												)}
												value={(field.state.value as string | undefined) ?? ""}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												maxLength={DESCRIPTION_MAX_LENGTH}
												rows={3}
												hint={
													<span className="block text-right text-xs text-text-tertiary">
														{(field.state.value as string | undefined)
															?.length ?? 0}
														/{DESCRIPTION_MAX_LENGTH}
													</span>
												}
											/>
										)}
									/>

									<section className="space-y-3">
										<div>
											<h3 className="text-sm font-semibold text-primary">
												{t(paths.formRules.modal.conditionsSection)}{" "}
												<span className="font-normal text-tertiary">
													({t(paths.formRules.modal.optional)})
												</span>
											</h3>
											<p className="mt-1 text-xs text-tertiary">
												{t(paths.formRules.modal.conditionsHint)}
											</p>
										</div>

										<form.Field name="conditionLogic">
											{(field) => (
												<FormRuleConditionLogicToggle
													value={field.state.value}
													onChange={(next) => {
														field.handleChange(next);
													}}
													paths={paths}
												/>
											)}
										</form.Field>

										<form.Field name="conditions" mode="array">
											{(arrayField) => (
												<div className="space-y-3">
													{arrayField.state.value.length === 0 ? (
														<p className="text-xs italic text-gray-500">
															{t(paths.formRules.modal.conditionsEmpty)}
														</p>
													) : null}

													{arrayField.state.value.length > 0 ? (
														<ul className="space-y-3">
															{arrayField.state.value.map(
																(c: FormRuleCondition, idx: number) => (
																	<ConditionRow
																		key={c.id}
																		form={form}
																		idx={idx}
																		fieldsById={fieldsById}
																		fieldSelectOptions={fieldSelectOptions}
																		operatorOptionsFor={operatorOptionsFor}
																		fieldLabel={t(
																			paths.formRules.modal.conditionFieldLabel,
																		)}
																		operatorLabel={t(
																			paths.formRules.modal
																				.conditionOperatorLabel,
																		)}
																		valueLabel={t(
																			paths.formRules.modal.conditionValueLabel,
																		)}
																		removeLabel={t(
																			paths.formRules.modal.removeCondition,
																		)}
																		selectFieldLabel={t(
																			paths.formRules.modal.selectField,
																		)}
																		selectOperatorLabel={t(
																			paths.formRules.modal.selectOperator,
																		)}
																		valuePlaceholder={t(
																			paths.formRules.modal.valuePlaceholder,
																		)}
																		onRemove={() => arrayField.removeValue(idx)}
																	/>
																),
															)}
														</ul>
													) : null}

													<Button
														type="button"
														size="sm"
														color="secondary"
														iconLeading={Plus}
														onClick={addCondition}
													>
														{t(paths.formRules.modal.addCondition)}
													</Button>
												</div>
											)}
										</form.Field>
									</section>

									<section className="space-y-3">
										<div>
											<h3 className="text-sm font-semibold text-primary">
												{t(paths.formRules.modal.actionsSection)}
												<span className="ml-0.5 text-error-primary">*</span>
											</h3>
											<p className="mt-1 text-xs text-tertiary">
												{t(paths.formRules.modal.actionsHint)}
											</p>
										</div>

										<form.Subscribe
											selector={(state) => state.values.actions.length}
										>
											{(count) =>
												count === 0 ? (
													<p className="text-xs text-error-primary">
														{t(paths.formRules.validation.actionsRequired)}
													</p>
												) : null
											}
										</form.Subscribe>

										<form.Field name="actions" mode="array">
											{(arrayField) => (
												<div className="space-y-3">
													{arrayField.state.value.length > 0 ? (
														<ul className="space-y-3">
															{arrayField.state.value.map(
																(a: FormRuleAction, idx: number) => (
																	<ActionRow
																		key={a.id}
																		form={form}
																		idx={idx}
																		actionTypeOptions={actionTypeOptions}
																		fieldSelectOptions={fieldSelectOptions}
																		actionTypeLabel={t(
																			paths.formRules.modal.actionTypeLabel,
																		)}
																		targetFieldLabel={t(
																			paths.formRules.modal.targetFieldLabel,
																		)}
																		removeLabel={t(
																			paths.formRules.modal.removeAction,
																		)}
																		onRemove={() => arrayField.removeValue(idx)}
																	/>
																),
															)}
														</ul>
													) : null}

													<Button
														type="button"
														size="sm"
														color="secondary"
														iconLeading={Plus}
														onClick={addAction}
													>
														{t(paths.formRules.modal.addAction)}
													</Button>
												</div>
											)}
										</form.Field>
									</section>
								</form>
							</div>

							<div className="z-10 flex flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
								<Button color="secondary" size="lg" onClick={onCancel}>
									{t(paths.formRules.modal.cancel)}
								</Button>
								<form.Subscribe
									selector={(state) => [state.isSubmitting] as const}
								>
									{([isSubmitting]) => (
										<Button
											color="primary"
											size="lg"
											isLoading={isSubmitting}
											showTextWhileLoading
											onClick={() => form.handleSubmit()}
										>
											{t(paths.formRules.modal.save)}
										</Button>
									)}
								</form.Subscribe>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
}

function RemoveRuleItemButton({
	removeLabel,
	onRemove,
}: {
	removeLabel: string;
	onRemove: () => void;
}) {
	return (
		<button
			type="button"
			className="shrink-0 self-center rounded-md p-1 text-tertiary hover:bg-secondary-subtle hover:text-primary"
			aria-label={removeLabel}
			onClick={onRemove}
		>
			<Trash02 size={16} />
		</button>
	);
}

interface ConditionRowProps {
	form: FormRuleEditorForm;
	idx: number;
	fieldsById: Map<string, FormRuleEditorFieldOption>;
	fieldSelectOptions: { value: string; label: string }[];
	operatorOptionsFor: (
		dataType: string | undefined,
	) => { value: string; label: string }[];
	fieldLabel: string;
	operatorLabel: string;
	valueLabel: string;
	removeLabel: string;
	selectFieldLabel: string;
	selectOperatorLabel: string;
	valuePlaceholder: string;
	onRemove: () => void;
}

function ConditionRow({
	form,
	idx,
	fieldsById,
	fieldSelectOptions,
	operatorOptionsFor,
	fieldLabel,
	operatorLabel,
	valueLabel,
	removeLabel,
	selectFieldLabel,
	selectOperatorLabel,
	valuePlaceholder,
	onRemove,
}: ConditionRowProps) {
	const fieldDefPath = `conditions[${idx}].fieldDefinitionId` as const;
	const operatorPath = `conditions[${idx}].operator` as const;
	const valuePath = `conditions[${idx}].value` as const;

	return (
		<li className={RULE_ITEM_CARD_CLASS}>
			<div className="flex items-center gap-3">
				<div className="min-w-0 flex-1 space-y-3">
					<form.Field name={fieldDefPath as never}>
						{(field) => (
							<NativeSelect
								className={RULE_FIELD_CONTROL_CLASS}
								label={fieldLabel}
								aria-label={selectFieldLabel}
								value={(field.state.value as string) ?? ""}
								onChange={(e) => {
									const fieldDefinitionId = e.target.value;
									field.handleChange(fieldDefinitionId as never);
									const fieldOption = fieldsById.get(fieldDefinitionId);
									const dataType = fieldOption?.dataType;
									const currentOperator = form.getFieldValue(
										operatorPath as never,
									) as FormRuleConditionOperatorValue;
									const nextOperator = ensureOperatorForField(
										currentOperator,
										dataType,
									);
									if (nextOperator !== currentOperator) {
										form.setFieldValue(
											operatorPath as never,
											nextOperator as never,
										);
									}
									const inputKind = getConditionValueInputKind(
										dataType,
										nextOperator,
										fieldOption?.options,
									);
									form.setFieldValue(
										valuePath as never,
										normalizeConditionValue(undefined, inputKind) as never,
									);
								}}
								onBlur={field.handleBlur}
								options={fieldSelectOptions}
								selectClassName="py-2 text-sm"
							/>
						)}
					</form.Field>

					<form.Subscribe
						selector={(s) => {
							const conditions = (s.values as FormRule).conditions;
							return conditions[idx]?.fieldDefinitionId ?? "";
						}}
					>
						{(fieldDefinitionId) => {
							const dataType = fieldsById.get(fieldDefinitionId)?.dataType;
							return (
								<form.Field name={operatorPath as never}>
									{(field) => (
										<NativeSelect
											className={RULE_FIELD_CONTROL_CLASS}
											label={operatorLabel}
											aria-label={selectOperatorLabel}
											value={(field.state.value as string) ?? ""}
											onChange={(e) => {
												const op = e.target
													.value as FormRuleConditionOperatorValue;
												field.handleChange(op as never);
												const fieldOption = fieldsById.get(fieldDefinitionId);
												const inputKind = getConditionValueInputKind(
													fieldOption?.dataType,
													op,
													fieldOption?.options,
												);
												const currentValue = form.getFieldValue(
													valuePath as never,
												) as string | number | boolean | string[] | undefined;
												form.setFieldValue(
													valuePath as never,
													normalizeConditionValue(
														currentValue,
														inputKind,
													) as never,
												);
											}}
											onBlur={field.handleBlur}
											options={operatorOptionsFor(dataType)}
											selectClassName="py-2 text-sm"
										/>
									)}
								</form.Field>
							);
						}}
					</form.Subscribe>

					<form.Subscribe
						selector={(s) => {
							const condition = (s.values as FormRule).conditions[idx];
							return [
								condition?.fieldDefinitionId ?? "",
								condition?.operator,
							] as const;
						}}
					>
						{([fieldDefinitionId, operator]) => {
							if (operator === undefined) {
								return null;
							}

							const fieldOption = fieldsById.get(fieldDefinitionId);
							const inputKind = getConditionValueInputKind(
								fieldOption?.dataType,
								operator,
								fieldOption?.options,
							);

							if (inputKind === "none") {
								return null;
							}

							return (
								<form.Field name={valuePath as never}>
									{(field) => {
										const rawValue = field.state.value as
											| string
											| number
											| boolean
											| string[]
											| undefined;

										if (inputKind === "option_single") {
											return (
												<FormRuleOptionSingleValueInput
													label={valueLabel}
													value={
														(typeof rawValue === "string"
															? rawValue
															: Array.isArray(rawValue)
																? (rawValue[0] ?? "")
																: "") ?? ""
													}
													options={fieldOption?.options ?? []}
													onChange={(next) => field.handleChange(next as never)}
													onBlur={field.handleBlur}
												/>
											);
										}

										if (inputKind === "option_multi") {
											const multiValue = Array.isArray(rawValue)
												? rawValue
												: typeof rawValue === "string" && rawValue
													? [rawValue]
													: [];
											return (
												<FormRuleOptionMultiValueInput
													label={valueLabel}
													value={multiValue}
													options={fieldOption?.options ?? []}
													placeholder={valuePlaceholder}
													onChange={(next) => field.handleChange(next as never)}
													onBlur={field.handleBlur}
												/>
											);
										}

										const textValue =
											typeof rawValue === "string"
												? rawValue
												: Array.isArray(rawValue)
													? rawValue.join(", ")
													: rawValue === undefined
														? ""
														: String(rawValue);

										return (
											<FormRuleTextValueInput
												label={valueLabel}
												value={textValue}
												placeholder={valuePlaceholder}
												onChange={(next) => field.handleChange(next as never)}
												onBlur={field.handleBlur}
											/>
										);
									}}
								</form.Field>
							);
						}}
					</form.Subscribe>
				</div>

				<RemoveRuleItemButton removeLabel={removeLabel} onRemove={onRemove} />
			</div>
		</li>
	);
}

interface ActionRowProps {
	form: FormRuleEditorForm;
	idx: number;
	actionTypeOptions: { value: string; label: string }[];
	fieldSelectOptions: { value: string; label: string }[];
	actionTypeLabel: string;
	targetFieldLabel: string;
	removeLabel: string;
	onRemove: () => void;
}

function ActionRow({
	form,
	idx,
	actionTypeOptions,
	fieldSelectOptions,
	actionTypeLabel,
	targetFieldLabel,
	removeLabel,
	onRemove,
}: ActionRowProps) {
	const typePath = `actions[${idx}].type` as const;
	const targetPath = `actions[${idx}].targetFieldDefinitionId` as const;

	return (
		<li className={RULE_ITEM_CARD_CLASS}>
			<div className="flex items-center gap-3">
				<div className="min-w-0 flex-1 space-y-3">
					<form.Field name={typePath as never}>
						{(field) => (
							<NativeSelect
								className={RULE_FIELD_CONTROL_CLASS}
								label={`${actionTypeLabel} *`}
								value={(field.state.value as string) ?? ""}
								onChange={(e) =>
									field.handleChange(
										e.target.value as FormRuleActionTypeValue as never,
									)
								}
								onBlur={field.handleBlur}
								options={actionTypeOptions}
								selectClassName="py-2 text-sm"
							/>
						)}
					</form.Field>

					<form.Field name={targetPath as never}>
						{(field) => (
							<NativeSelect
								className={RULE_FIELD_CONTROL_CLASS}
								label={`${targetFieldLabel} *`}
								value={(field.state.value as string) ?? ""}
								onChange={(e) => field.handleChange(e.target.value as never)}
								onBlur={field.handleBlur}
								options={fieldSelectOptions}
								selectClassName="py-2 text-sm"
							/>
						)}
					</form.Field>
				</div>

				<RemoveRuleItemButton removeLabel={removeLabel} onRemove={onRemove} />
			</div>
		</li>
	);
}
